import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bool, func, number, object, shape, string, oneOfType } from 'prop-types';
import { numberWithCommas } from 'ps-components/lib/formatters';

import { actions as jobMatchingActions } from '../../reducers/modules/jobs/jobMatching/jobMatching';
import { actions as organizationStatisticsActions } from '../../reducers/modules/organization/organizationStatistics/organizationStatistics';
import { actions as surveyActivityActions } from '../../reducers/modules/surveys/surveyActivity/surveyActivity';

import DataNugget from 'ps-components/StatisticsNugget/DataNugget/DataNugget';
import LoadingMarketAnalysis from '../../components/Home/LoadingStates/LoadingMarketAnalysis';
import MarketAnalysis from '../../containers/MarketAnalysis/MarketAnalysis';
import MarketPricing from '../../containers/MarketPricing/MarketPricing';
import PricingTrends from '../../containers/PricingTrends/PricingTrends';
import RecentReports from '../Report/RecentReports/RecentReports';
import StatisticsBar from '../../components/Home/StatisticsBar/StatisticsBar';
import StatisticsNugget from 'ps-components/StatisticsNugget/StatisticsNugget';
import SurveyUtilization from '../Survey/SurveyUtilization/SurveyUtilization';

import './Home.scss';

const EMPLOYEES_URL = '/iframe/merit/hris/employee/employees';
const JOBS_URL = '/iframe/merit/hris/job/jobs';
const MARKET_PRICING_URL = '/iframe/merit/pricing2/price';
export const NEW_MARKET_PRICING_URL = '/market-analysis/market-pricing';
const STRUCTURES_URL = '/iframe/merit/hris/structures/structures';
const REPORT_LIBRARY_URL = '/reports/library';
const AD_HOC_PRICING_URL = '/market-analysis/jobs/adhoc/survey-library';

class Home extends Component {
  static propTypes = {
    activeSurveys: number,
    ageToDate: string,
    companyCount: number,
    dispatch: func,
    firstName: string,
    groupId: oneOfType([string, number]),
    history: object,
    inactiveSurveys: number,
    isJobMatchingLoading: bool,
    isOrganizationStatisticsLoading: bool,
    isSurveyActivityLoading: bool,
    location: object,
    match: object,
    orgCounts: shape({
      employees: number,
      jobs: number,
      salaryRanges: number
    }),
    surveyCount: number,
    surveyYear: number,
    permissions: shape({
      reportViewUser: bool,
      restrictedUser: bool
    }),
    launchDarklyFeatureFlags: object
  };

  constructor(props) {
    super(props);

    this.getHomeData = this.getHomeData.bind(this);
    this.renderMarketAnalysis = this.renderMarketAnalysis.bind(this);
    this.renderPricingTrends = this.renderPricingTrends.bind(this);
    this.renderStatisticsNuggets = this.renderStatisticsNuggets.bind(this);
  }

  componentDidMount() {
    const { permissions } = this.props;

    if (permissions.reportViewUser) {
      this.props.history.replace({
        pathname: REPORT_LIBRARY_URL
      });
    } else if (permissions.restrictedUser) {
      const marketPricingUrl = this.props.location.pathname.startsWith('/market-analysis/jobs/')
        ? NEW_MARKET_PRICING_URL
        : MARKET_PRICING_URL;
      this.props.history.replace({
        pathname: marketPricingUrl
      });
    } else {
      this.getHomeData();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { ageToDate, groupId } = this.props;

    if (this.props === nextProps) return;
    if (groupId !== nextProps.groupId || ageToDate !== nextProps.ageToDate) this.getHomeData();
  }

  shouldComponentUpdate(nextProps) {
    return this.props !== nextProps;
  }

  pushHistory(path) {
    const { history } = this.props;

    history.push({ pathname: path });
  }

  getHomeData() {
    const { dispatch } = this.props;
    jobMatchingActions.getJobMatches(dispatch);
    organizationStatisticsActions.getOrganizationStatistics(dispatch);
    surveyActivityActions.getSurveyActivity(dispatch);
  }

  renderMarketAnalysis() {
    const { employees } = this.props.orgCounts;

    return (
      <MarketAnalysis employeesCount={employees} onTotalEmployeesClick={this.pushHistory.bind(this, EMPLOYEES_URL)} />
    );
  }

  renderPricingTrends() {
    const { surveyYear } = this.props;

    return <PricingTrends pricingYear={surveyYear} />;
  }

  renderStatisticsNuggets() {
    const { activeSurveys, companyCount, inactiveSurveys, orgCounts, surveyCount } = this.props;
    const emptyNuggetLabel = '';
    const emptyNuggetValue = '-';

    return (
      <React.Fragment>
        <StatisticsNugget title="Jobs Matched">
          <DataNugget
            label="Survey"
            labelTooltip="Survey jobs matched to Company jobs"
            value={surveyCount ? numberWithCommas(surveyCount) : emptyNuggetValue}
          />
          <DataNugget
            label="Company"
            labelTooltip="Company jobs matched to Survey jobs"
            value={companyCount ? numberWithCommas(companyCount) : emptyNuggetValue}
          />
        </StatisticsNugget>
        <StatisticsNugget title="Survey Activity">
          <DataNugget label="Active" value={activeSurveys ? activeSurveys : emptyNuggetValue} />
          <DataNugget label="Inactive" value={inactiveSurveys ? inactiveSurveys : emptyNuggetValue} />
        </StatisticsNugget>
        <StatisticsNugget title="Jobs" titleClick={this.pushHistory.bind(this, JOBS_URL)}>
          <DataNugget
            label={emptyNuggetLabel}
            value={orgCounts && orgCounts.jobs ? numberWithCommas(orgCounts.jobs) : emptyNuggetValue}
          />
        </StatisticsNugget>
        <StatisticsNugget title="Salary Ranges" titleClick={this.pushHistory.bind(this, STRUCTURES_URL)}>
          <DataNugget
            label={emptyNuggetLabel}
            value={orgCounts && orgCounts.salaryRanges ? numberWithCommas(orgCounts.salaryRanges) : emptyNuggetValue}
          />
        </StatisticsNugget>
      </React.Fragment>
    );
  }

  render() {
    const {
      firstName,
      isJobMatchingLoading,
      isOrganizationStatisticsLoading,
      isSurveyActivityLoading,
      orgCounts,
      surveyYear,
      launchDarklyFeatureFlags,
      permissions
    } = this.props;
    const isLoading = !isSurveyActivityLoading && !isOrganizationStatisticsLoading && !isJobMatchingLoading;

    return (
      <div className="homelayout">
        <div className="homelayout__main">
          <div className="home__welcome">{`Welcome back ${firstName ? firstName : ''}`}</div>
          <MarketPricing
            priceJobClick={this.pushHistory.bind(this, NEW_MARKET_PRICING_URL)}
            adHocPricingClick={this.pushHistory.bind(this, AD_HOC_PRICING_URL)}
            pricingYear={surveyYear ? surveyYear : null}
            launchDarklyFeatureFlags={launchDarklyFeatureFlags}
            permissions={permissions}
          />
          <StatisticsBar>{isLoading ? this.renderStatisticsNuggets() : null}</StatisticsBar>
          {isOrganizationStatisticsLoading && isSurveyActivityLoading && <LoadingMarketAnalysis />}
          {orgCounts && orgCounts?.employees && this.renderMarketAnalysis()}
          {surveyYear && !orgCounts?.employees && this.renderPricingTrends()}
        </div>
        <div className="homelayout__list">
          <SurveyUtilization />
          <RecentReports />
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ aging, group, jobMatching, organizationStatistics, surveyActivity, session }) => ({
  activeSurveys: surveyActivity.data.activeSurveys,
  ageToDate: aging.ageToDate,
  companyCount: jobMatching.jobMatching.companyCount,
  firstName: organizationStatistics.data.user.firstName,
  groupId: group.groupId,
  inactiveSurveys: surveyActivity.data.inactiveSurveys,
  isJobMatchingLoading: jobMatching.loading,
  isOrganizationStatisticsLoading: organizationStatistics.loading,
  isSurveyActivityLoading: surveyActivity.loading,
  orgCounts: organizationStatistics.data.statistics.orgCounts,
  surveyCount: jobMatching.jobMatching.surveyCount,
  surveyYear: session.benchmarkYear,
  permissions: session.user.rights
});

export default connect(mapStateToProps)(Home);
